import React, { useEffect, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Edit, Inject } from '@syncfusion/ej2-react-grids';
import Swal from 'sweetalert2';
import { Header } from '../components';
import { MdDeleteForever, MdEditNote, MdInsertDriveFile } from "react-icons/md";

const Notices = () => {
  const [noticesData, setNoticesData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditNotice, setCurrentEditNotice] = useState(null);
  const [newNotice, setNewNotice] = useState({
    title: '',
    date: '',
    pdf_url: '',
    _id: null
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration missing');
      }
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/notices', {
        headers: {
          'api-key': process.env.REACT_APP_API_KEY
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }

      const reversedData = [...data].reverse();
      setNoticesData(reversedData);
    } catch (error) {
      console.error('Error fetching notices:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch notices',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleEditNotice = (notice) => {
    setIsEditMode(true);
    setShowAddForm(true);
    const formattedDate = new Date(notice.date).toISOString().split('T')[0];
    setNewNotice({
      title: notice.title,
      date: formattedDate,
      pdf_url: notice.pdf_url,
      _id: notice._id
    });
    setCurrentEditNotice(notice);
  };

  const handleAddNotice = async (e) => {
    e.preventDefault();

    try {
      // Validate input fields
      if (!newNotice.title?.trim() || !newNotice.date?.trim() || (!newNotice.pdf && !isEditMode)) {
        await Swal.fire({
          title: 'Error!',
          text: 'All fields are required',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
        return;
      }

      // Check API configuration
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration is missing');
      }

      let uploadedPdfName = newNotice.pdf_url;

      // Step 1: If editing and a new file is selected, delete the old file
      if (isEditMode && newNotice.pdf && newNotice.pdf_url) {

        const deleteResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/pdfupload/${newNotice.pdf_url}`, // Endpoint to delete the old file
          {
            method: 'DELETE',
            headers: {
              'api-key': process.env.REACT_APP_API_KEY,
            },
          }
        );

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to delete previous PDF');
        }
      }

      // Step 2: Upload the new PDF file if a new one is selected
      if (newNotice.pdf) {
        const pdfFile = newNotice.pdf; // Assuming `newNotice.pdf` contains the file object
        const formData = new FormData();
        formData.append('file', pdfFile);

        const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/pdfupload`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to upload PDF');
        }

        const uploadResult = await uploadResponse.json();
        uploadedPdfName = uploadResult.filename; // Assuming the API returns the file name
      }

      // Step 3: Prepare notice data
      const formattedDate = new Date(newNotice.date).toISOString().split('T')[0];
      const noticeData = {
        title: newNotice.title,
        date: formattedDate,
        pdf_url: uploadedPdfName, // Store only the new PDF file name
      };

      // Step 4: Add or update notice
      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/api/notices/${newNotice._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/notices`;

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(noticeData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to add/update notice');
      }

      // Success handling
      await Swal.fire({
        title: 'Success!',
        text: isEditMode ? 'Notice updated successfully' : 'Notice added successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });

      // Reset form and refresh data
      setShowAddForm(false);
      setIsEditMode(false);
      setNewNotice({ title: '', date: '', pdf: null });
      await fetchNotices();
    } catch (error) {
      console.error('Error adding/updating notice:', error);

      // Error handling
      await Swal.fire({
        title: 'Error!',
        text: error.message || 'An unexpected error occurred',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    }
  };



  const handleDelete = async (id, pdfFilename) => {
    try {
      // Check for API configuration
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error("API configuration missing");
      }

      // Delete the notice
      const noticeResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notices/${id}`,
        {
          method: "DELETE",
          headers: {
            "api-key": process.env.REACT_APP_API_KEY,
          },
        }
      );

      if (!noticeResponse.ok) {
        throw new Error("Failed to delete notice");
      }

      // If notice deletion succeeds, delete the associated PDF file
      if (pdfFilename) {

        const uploadResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/pdfupload/${pdfFilename}`,
          {
            method: "DELETE",
          }
        );

        if (!uploadResponse.ok) {
          throw new Error("Failed to delete associated PDF file");
        }
      }

      // Refresh the notices list
      await fetchNotices();
      return true;
    } catch (error) {
      console.error("Error deleting notice or associated file:", error);
      throw error;
    }
  };

  const noticesGrid = [
    {
      field: 'title',
      headerText: 'Title',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'date',
      headerText: 'Date',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'pdf_url',
      headerText: 'PDF URL',
      textAlign: 'Center',
      width: '150',
      template: (props) => (
        <div>
          {props.pdf_url && (
            <a href={`${process.env.REACT_APP_UPLOADS_URL}/${props.pdf_url}`} target="_blank" rel="noopener noreferrer">
              <button className="bg-orange-400 text-white px-1 py-1 text-xl rounded mr-2">
                <MdInsertDriveFile />
              </button>
            </a>
          )}
        </div>
      )
    },
    {
      field: 'createdAt',
      headerText: 'Created At',
      width: '120',
      textAlign: 'Center',
    },
    {
      field: 'updatedAt',
      headerText: 'Updated At',
      width: '120',
      textAlign: 'Center',
    },
    {
      field: '_id',
      headerText: 'Actions',
      width: '150',
      textAlign: 'Center',
      template: (props) => (
        <div>
          <button
            className="bg-blue-500 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => handleEditNotice(props)}
          >
            <MdEditNote />
          </button>
          <button
            className="bg-red-500 text-white px-1 py-1 text-xl rounded"
            onClick={() => {
              Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
              }).then(async (result) => {
                if (result.isConfirmed) {
                  try {
                    await handleDelete(props._id, props.pdf_url);

                    Swal.fire({
                      title: 'Deleted!',
                      text: 'Notice has been deleted.',
                      icon: 'success',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  } catch (error) {
                    Swal.fire({
                      title: 'Error!',
                      text: 'Failed to delete the notice or associated file.',
                      icon: 'error',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  }
                }
              });
            }}
          >
            <MdDeleteForever />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="m-2 md:m-10 sm:mt-24 p-2 md:p-4 bg-white rounded-3xl">
      <div className="flex justify-between items-center mb-4">
        <Header category="Page" title="Notices" />
        <button
          onClick={() => {
            setShowAddForm(true);
            setIsEditMode(false);
            setNewNotice({ title: '', date: '', pdf: null });
          }}
          className="bg-green-500 text-white px-2 py-2 rounded"
        >
          Add Notice
        </button>
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-[600px]">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">
              {isEditMode ? 'Edit Notice' : 'Add New Notice'}
            </h2>
            <form onSubmit={handleAddNotice} className="space-y-6">
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Title:</label>
                <input
                  type="text"
                  value={newNotice.title}
                  onChange={(e) => setNewNotice({ ...newNotice, title: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter notice title"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Date:</label>
                <input
                  type="date"
                  value={newNotice.date}
                  onChange={(e) => setNewNotice({ ...newNotice, date: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">PDF URL:</label>

                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      const file = e.target.files[0];

                      // Validate file size (max 400KB)
                      if (file.size > 400 * 1024) {
                        Swal.fire({
                          icon: "error",
                          title: "File Too Large",
                          text: "The selected PDF must be less than 400KB.",
                          confirmButtonText: "Okay",
                          confirmButtonColor: "#3085d6",
                        });
                        e.target.value = ""; // Reset the input field
                        return;
                      }

                      setNewNotice({ ...newNotice, pdf: file });
                    }
                  }}
                  className="border border-gray-300 p-2 rounded w-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  {...(isEditMode ? {} : { required: true })}
                />

                {isEditMode && (
                  <div className="text-sm text-gray-600 mt-2">
                    Current file: {newNotice.pdf_url}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setIsEditMode(false);
                    setNewNotice({ title: '', date: '', pdf: null });
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
                >
                  {isEditMode ? 'Update Notice' : 'Add Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <GridComponent
        id="gridcomp"
        dataSource={noticesData}
        allowPaging
        allowSorting
      >
        <ColumnsDirective>
          {noticesGrid.map((item, index) => (
            <ColumnDirective key={index} {...item} />
          ))}
        </ColumnsDirective>
        <Inject services={[Resize, Sort, ContextMenu, Filter, Page, ExcelExport, Edit, PdfExport]} />
      </GridComponent>
    </div>
  );
};

export default Notices;